import { Grid, TextField } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { ButtonGroup } from "../../components/buttonGroup/ButtonGroup";
import { ButtonTheme } from "../../components/buttonTheme/ButtonTheme";
import { ConfirmationArea } from "../../components/confirmationArea/ConfirmationArea";
import { Form } from "../../components/form/Form";
import { PageCard } from "../../components/pageCard/PageCard";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { IFormErrors } from "../../interfaces/IFormErrors";
import { IPageProps } from "../../interfaces/IPageProps";
import { ISpecie } from "../../interfaces/ISpecie";
import api from "../../services/api";
import getValidationError from "../../utils/getValidationError";

export const Specie: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const navigate = useNavigate();
  const location = useLocation();
  const toastr = useToastr();
  const { signOut } = useAuth();

  const [specieId, setSpecieId] = useState("");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formErrors, setFormErrors] = useState<IFormErrors>({});

  const handleListSpecie = useCallback(async () => {
    const id = location.pathname.replace("/specie", "").replace("/", "");

    if (id) {
      setSpecieId(id);
      setLoading(true);

      await api
        .get(`/species/${id}`)
        .then(response => {
          const specie: ISpecie = response.data;

          setName(specie.name ? specie.name : "");
          setDescription(specie.description ? specie.description : "");
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [navigate, signOut, toastr, location.pathname]);

  const handleDeleteSpecie = useCallback(async () => {
    const id = location.pathname.replace("/specie", "").replace("/", "");

    if (id) {
      setLoading(true);

      await api
        .delete(`/species/${id}`)
        .then(() => {
          navigate("/species");
          toastr.success("Espécie excluída com sucesso");
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [navigate, signOut, toastr, location.pathname]);

  const handleSubmit = useCallback(async () => {
    try {
      setFormErrors({});

      const schema = Yup.object().shape({
        name: Yup.string().required("Nome é obrigatório"),
      });

      if (specieId) {
        const data: ISpecie = {
          name,
          description,
        };

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .put(`/species/${specieId}`, data)
          .then(async () => {
            await handleListSpecie();
            toastr.success("Espécie atualizada com sucesso");
            navigate("/species");
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        const data: ISpecie = {
          name,
          description,
        };

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .post(`/species`, data)
          .then(() => {
            toastr.success("Espécie cadastrada com sucesso");
            navigate("/species");
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } catch (error: Yup.ValidationError | any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationError(error);
        setFormErrors(errors);
        return;
      }
      toastr.error(
        error.response.data?.message || "Contate a equipe de suporte",
      );
    } finally {
      setLoading(false);
    }
  }, [
    description,
    handleListSpecie,
    name,
    navigate,
    signOut,
    specieId,
    toastr,
  ]);

  useEffect(() => {
    handleListSpecie();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TitleContainer>
        <h1>Espécie</h1>
      </TitleContainer>
      <PageCard>
        <Form>
          <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Nome"
                value={name}
                required
                onChange={e => setName(e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Descrição"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </Grid>
          </Grid>
          <ButtonGroup justformobilie>
            <ButtonTheme
              onClick={handleSubmit}
              disabled={loading}
              loading={loading}
            >
              Salvar
            </ButtonTheme>
            {specieId && (
              <ButtonTheme
                onClick={() => setDialogDeleteOpen(true)}
                disabled={loading}
                loading={loading}
                color="error"
              >
                Excluir
              </ButtonTheme>
            )}
          </ButtonGroup>
        </Form>

        <ConfirmationArea
          dialogOpen={dialogDeleteOpen}
          id={specieId}
          title="Deseja realmente excluir o item?"
          message=""
          key={`dialog-${specieId}`}
          handleConfirmation={handleDeleteSpecie}
          deny={() => setDialogDeleteOpen(false)}
        />
      </PageCard>
    </>
  );
};
